/* Print page left column + centre preview — desktop PrintItemCard / NestThumb / drop zone / single-image preview. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { EmptyState, Icon } from '../../ui/index.jsx';
import { NEST_DRAG_TYPE } from './nest/NestCanvas.jsx';
import { ZoomBar, bindDragPan, bindWheelZoom, clampZoom } from './ZoomBar.jsx';
import { useTranslation } from 'react-i18next';

const ACCEPT = '.png,.jpg,.jpeg,.webp,.pdf,image/png,image/jpeg,image/webp,application/pdf';

export function DropZone({ onFiles, disabled }) {
  const { t } = useTranslation('bv');
  const input = useRef(null);
  const [over, setOver] = useState(false);
  const [tip, setTip] = useState(false);
  return (
    <div className="bv-dropwrap" onMouseEnter={() => setTip(true)} onMouseLeave={() => setTip(false)}>
      <button type="button" id="upload" className={`bv-drop${over ? ' is-over' : ''}`} disabled={disabled} data-testid="drop-zone"
        onClick={() => input.current && input.current.click()}
        onDragOver={(e) => { if (disabled) return; e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) onFiles([...e.dataTransfer.files]); }}>
        <Icon name="upload" size={30} className="bv-drop__icon" />
        <span className="bv-drop__sub">{disabled ? t('print.drop.disabled') : t('print.drop.hint')}</span>
        <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={(e) => { onFiles([...e.target.files]); e.target.value = ''; }} />
      </button>
      {disabled && tip && (
        <div className="bv-droptip" role="tooltip">
          <b>{t('print.drop.setup')}</b>
          <span>{t('print.drop.step1')}</span>
          <span>{t('print.drop.step2')}</span>
          <span>{t('print.drop.step3')}</span>
          <span>{t('print.drop.then')}</span>
        </div>
      )}
    </div>
  );
}

const short = (n) => (n.length > 30 ? `${n.slice(0, 30)}...` : n);

/**
 * One card. The page owns all state; the card renders `item` and calls back.
 * item.orientation: portrait | landscape | custom; item.draft = {w, h, lock} (custom inputs); item.custom = {w, h} once → was pressed.
 */
export function ItemCard({
  item, active, busy, canPrint, printEnabled, printLabel, onActivate, onOrientation, onDraft, onGenerate, onPrint,
  showNest, nestEnabled, onNest, onDelete, status, customAllowed, minH, sendJob, sending,
}) {
  const { t } = useTranslation('bv');
  const showCustom = active && item.orientation === 'custom';
  const d = item.draft || {};
  const aspect = item.w && item.h ? item.h / item.w : null;
  const hFromW = (w) => { const v = parseFloat(w); return aspect && Number.isFinite(v) ? Math.round(Math.min(v * aspect, 60) * 100) / 100 : null; };
  const stop = (e) => e.stopPropagation();
  return (
    <div className={`bv-itemcard${active ? ' is-active' : ''}`} data-testid="file-card" onClick={() => !busy && onActivate()} role="group" aria-label={item.name}>
      <div className="bv-itemcard__thumbbox">
        <img className={`bv-itemcard__thumb${item.orientation === 'landscape' ? ' is-turned' : ''}`} src={item.url} alt="thumbnail" draggable={false} />
        {item.uploading && <span className="bv-itemcard__state">{t('print.card.uploading')}</span>}
        {item.error && <span className="bv-itemcard__state is-error">{t('print.card.cantUse')}</span>}
      </div>
      <div className="bv-itemcard__name" title={item.name}>{short(item.name)}</div>
      <select className="bv-select bv-itemcard__orient" aria-label={t('print.card.orientation')} value={item.orientation} disabled={!active || busy} onClick={stop}
        onChange={(e) => onOrientation(e.target.value)}>
        <option value="portrait">{t('print.portrait')}</option>
        <option value="landscape">{t('print.landscape')}</option>
        <option value="custom" disabled={!customAllowed}>{customAllowed ? t('print.card.custom') : t('print.card.customRollOnly')}</option>
      </select>
      {showCustom && (
        <div className="bv-custom" onClick={stop}>
          <span className="bv-custom__lbl">W</span>
          <input type="number" className="bv-input bv-custom__num" aria-label={t('print.widthIn')} value={d.w ?? ''} min="8" max="60" step="0.01" disabled={busy}
            onFocus={(e) => e.target.select()}
            onChange={(e) => { const v = e.target.value; const h = d.lock ? hFromW(v) : null; onDraft(h != null ? { w: v, h } : { w: v }); }} />
          <span className="bv-custom__x">×</span>
          <span className="bv-custom__lbl">H</span>
          <input type="number" className="bv-input bv-custom__num" aria-label={t('print.heightIn')} value={d.h ?? ''} min={minH} max="60" step="0.01" disabled={d.lock || busy}
            onFocus={(e) => e.target.select()} onChange={(e) => onDraft({ h: e.target.value })} />
          <button type="button" className="bv-custom__go" title={t('print.card.generate')} aria-label={t('print.card.generate')} disabled={busy} onClick={() => onGenerate(parseFloat(d.w), parseFloat(d.h))}>→</button>
        </div>
      )}
      {showCustom && (
        <label className="bv-aspect" onClick={stop}>
          <input type="checkbox" checked={!!d.lock} disabled={busy}
            onChange={(e) => { const on = e.target.checked; const h = on ? hFromW(d.w) : null; onDraft(h != null ? { lock: on, h } : { lock: on }); }} />
          <span className="bv-aspect__track" aria-hidden="true"><span /></span>
          <span>{t('print.card.aspect')}</span>
        </label>
      )}
      {item.custom && !showCustom && <div className="bv-small bv-muted">{`${item.custom.w}" × ${item.custom.h}"`}</div>}
      <div className="bv-itemcard__btns" onClick={stop}>
        {sendJob ? (
          <button type="button" className="bv-cardbtn" disabled={!active || busy || sending} title={t('print.card.approvalTitle')} onClick={onPrint} data-testid="approval-button">
            {sending ? t('print.card.sending') : t('print.card.sendJob')}
          </button>
        ) : canPrint ? (
          <button type="button" className="bv-cardbtn bv-cardbtn--print" disabled={!active || !printEnabled || busy} onClick={onPrint} data-testid="print-button">{printLabel}</button>
        ) : null}
        {showNest && (
          <button type="button" className="bv-cardbtn" disabled={busy || !nestEnabled} onClick={onNest} data-testid="nest-button"
            title={nestEnabled ? t('print.nest.title') : t('print.nest.needRollAndTwo')}>{t('print.nest.nest')}</button>
        )}
        <button type="button" className="bv-cardbtn bv-cardbtn--del" title={t('print.card.deleteImage')} aria-label={t('print.card.deleteNamed', { name: item.name })} disabled={!active || busy} onClick={onDelete}><Icon name="trash" size={16} /></button>
      </div>
      {active && status && <div className="bv-itemcard__status bv-small bv-muted">{status}</div>}
    </div>
  );
}

export function NestThumb({ item, num, placed, selected, busy, onClick }) {
  const { t } = useTranslation('bv');
  return (
    <button type="button" className={`bv-nthumb${placed ? ' is-placed' : ''}${selected ? ' is-selected' : ''}`} draggable={!busy} data-testid="nest-thumb"
      onDragStart={(e) => {
        if (busy) { e.preventDefault(); return; }
        e.dataTransfer.setData(NEST_DRAG_TYPE, item.id);
        e.dataTransfer.effectAllowed = 'copyMove';
        const img = e.currentTarget.querySelector('img');
        if (img && img.complete) e.dataTransfer.setDragImage(img, img.width / 2, img.height / 2);
      }}
      onClick={busy ? undefined : onClick} aria-pressed={!!selected}
      title={placed ? t('print.nest.thumbPlaced') : t('print.nest.thumbAdd')}>
      {placed && <span className="bv-nthumb__num">{num}</span>}
      <img src={item.url} alt={item.name} draggable={false} />
      {!placed && <span className="bv-nthumb__add" aria-hidden="true">+</span>}
    </button>
  );
}

const fmt = (n, d = 1) => (Number.isFinite(n) ? Number(n).toFixed(d) : '–');
/** The viewport's inner size (ResizeObserver); the paper is scaled to fit it, then multiplied by the zoom. */
function useViewportSize() {
  const [box, setBox] = useState({ w: 0, h: 0 });
  const ro = useRef(null);
  const ref = useRef(null);
  // Stable callback ref: the viewport element changes when the preview switches branch (empty → plain → sized).
  // Measured with getBoundingClientRect (not clientWidth), so a scrollbar appearing while zoomed never feeds back
  // into the fit size — that feedback froze the page (2026-10-19).
  const setRef = useCallback((el) => {
    if (ro.current) { ro.current.disconnect(); ro.current = null; }
    ref.current = el;
    if (!el) return;
    const measure = () => { const r = el.getBoundingClientRect(); const w = Math.floor(r.width), h = Math.floor(r.height); setBox((b) => (b.w === w && b.h === h ? b : { w, h })); };
    measure();
    ro.current = new ResizeObserver(measure); ro.current.observe(el);
  }, []);
  return [box, setRef, ref];
}

export function Preview({ item, size, geom }) {
  const { t } = useTranslation('bv');
  const [{ w: vw, h: vh }, setVp, vp] = useViewportSize();
  const [zoom, setZoom] = useState(1);                 // 1 = fit the viewport
  const zoomRef = useRef(1); zoomRef.current = zoom;
  useEffect(() => { setZoom(1); }, [item && item.id, size && size.paperWidthIn, size && size.sheet]);   // new picture / paper → fit
  useEffect(() => { const a = bindWheelZoom(vp.current, () => zoomRef.current, setZoom); const b = bindDragPan(vp.current); return () => { a(); b(); }; }, [item && item.id, !!size]);
  if (!item) {
    return (
      <div className="bv-pv bv-pv--empty" data-testid="print-preview">
        <EmptyState icon="image" title={t('print.preview.emptyTitle')}>{t('print.preview.emptyBody')}</EmptyState>
      </div>
    );
  }
  if (!size) {
    return (
      <div className="bv-pv" data-testid="print-preview">
        <div className={`bv-pv__viewport${zoom > 1 ? ' is-zoomed' : ''}`} ref={setVp}><div className="bv-pv__content"><img className="bv-pv__plain" src={item.url} alt="preview" style={{ maxWidth: Math.max(0, vw - 32) * zoom || undefined, maxHeight: Math.max(0, vh - 32) * zoom || undefined }} /></div></div>
        <div className="bv-pv__foot"><p className="bv-pv__caption">{item.name}</p><ZoomBar zoom={zoom} onZoom={(z) => setZoom(clampZoom(z))} /></div>
      </div>
    );
  }
  const pw = size.paperWidthIn;
  const sheetH = size.sheet ? size.sheet.height : null;
  const ph = sheetH || Math.max(size.heightIn + 2 * (geom.minMarginIn || 0), 1);
  // fit: the viewport minus its padding, the height line column (right) and the feeder + width line (top/bottom)
  const fitW = Math.max(120, vw - 32 - 72), fitH = Math.max(120, vh - 24 - 44 - 40);
  const scale = Math.min(fitW / pw, fitH / ph) * zoom;
  const paperW = pw * scale; const paperH = ph * scale;
  const m = (size.custom ? 0 : geom.minMarginIn || 0) * scale;
  const imgW = size.widthIn * scale; const imgH = size.heightIn * scale;
  const turned = item.orientation === 'landscape' && !size.custom;
  const left = size.custom ? 0 : (paperW - imgW) / 2;
  const top = size.sheet ? m : m;
  return (
    <div className="bv-pv" data-testid="print-preview">
      <div className={`bv-pv__viewport${zoom > 1 ? ' is-zoomed' : ''}`} ref={setVp} data-testid="preview-viewport">
        <div className="bv-pv__content">
          <div className="bv-pv__feeder" style={{ width: paperW }}><span>Printer</span><div className="bv-pv__bar"><i /><i /></div></div>
          <div className="bv-pv__row">
            <div className="bv-pv__col">
              <div className={`bv-pv__paper${size.sheet ? ' is-sheet' : ''}`} style={{ width: paperW, height: paperH, '--m': `${m}px` }}>
                <div className="bv-pv__img" style={{ left, top, width: imgW, height: imgH }}>
                  <img src={item.url} alt="preview" style={turned ? { width: imgH, height: imgW, transform: 'translate(-50%, -50%) rotate(-90deg)' } : { width: imgW, height: imgH, transform: 'translate(-50%, -50%)' }} />
                </div>
              </div>
              <div className="bv-pv__dimw" style={{ width: paperW }}><span>{`${fmt(pw, pw % 1 ? 1 : 0)}"`}</span></div>
            </div>
            {size.sheet && size.sheet.height && paperH - top - imgH > 24 ? (
              <div className="bv-pv__dimh--sheet" style={{ marginTop: top }} data-testid="preview-dimh">
                <div className="is-img" style={{ height: imgH }}><span>{`${fmt(size.heightIn)}"`}</span></div>
                <div className="is-blank" style={{ height: paperH - top - imgH }} title={t('print.preview.sheetBlank', { h: fmt(size.sheet.height, size.sheet.height % 1 ? 2 : 0) })}><span>{`${fmt(size.sheet.height, size.sheet.height % 1 ? 2 : 0)}"`}</span></div>
              </div>
            ) : (
              <div className="bv-pv__dimh" style={{ height: imgH, marginTop: top }} data-testid="preview-dimh"><span>{`${fmt(size.heightIn)}"`}</span></div>
            )}
          </div>
        </div>
      </div>
      <div className="bv-pv__foot"><p className="bv-pv__caption">{item.name}</p><ZoomBar zoom={zoom} onZoom={(z) => setZoom(clampZoom(z))} /></div>
    </div>
  );
}
